import {
  ErrorBoundary,
  Icon,
  IconNames,
  ItemStatus,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  StatusProxy,
  Tree,
} from "@focus/focusUI";

import { DocumentViewer } from "./DocumentViewer";
import { useTree } from "../hooks/useTree";
import { buildSumTree } from "../utils/sumTree";
import { useSystemConfig } from "../hooks/useSystemConfig";
import { useEffect, useMemo } from "react";
import type { SUMTreeNodeData } from "../types/sum";

import "./HeaderHelpDialog.scss";

export function HeaderHelpDialog() {
  const { selected, collapsed, selectNode, expandNode, collapseNode } =
    useTree<SUMTreeNodeData>();

  const {
    sumConfiguration,
    loadSumConfiguration,
    getRequestStatus,
    getRequestError,
  } = useSystemConfig();

  const tree = useMemo(() => {
    if (!sumConfiguration) return [];
    return buildSumTree({
      values: sumConfiguration.items,
      collapsed,
      selectedId: selected?.id,
    });
  }, [sumConfiguration, collapsed, selected]);

  const path =
    selected?.href?.charAt(0) === "/"
      ? selected.href.substring(1)
      : selected?.href;

  useEffect(() => {
    loadSumConfiguration();
  }, []);

  const status = getRequestStatus("getSumConfiguration");
  const error = getRequestError("getSumConfiguration");

  return (
    <StatusProxy status={status} error={error}>
      <ErrorBoundary>
          <ResizablePanelGroup
            className="flex data-[panel-group-direction=vertical]:flex-col"
            direction="horizontal"
          >
            <ResizablePanel minSize={10} defaultSize={25} maxSize={35}>
              {status === ItemStatus.READY && (
                <div style={{ minHeight: "80vh", maxHeight: "80vh", overflow: "auto" }}>
                  <Tree
                    contents={tree}
                    onNodeClick={selectNode}
                    onNodeExpand={(node) => expandNode(node.id as string)}
                    onNodeCollapse={(node) => collapseNode(node.id as string)}
                  />
                </div>
              )}
            </ResizablePanel>

            <ResizableHandle />

            <ResizablePanel>
              {selected && path ? (
                <div className="flex flex-col w-full h-full">
                  <DocumentViewer path={path} />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center w-full h-full text-gray-400 select-none">
                  <Icon
                    icon={IconNames.DOCUMENT}
                    size={48}
                    className="mb-4 text-gray-300"
                  />
                  <span className="text-lg font-medium">No file selected</span>
                  <span className="text-sm text-gray-500 mt-1">
                    Select a file from the tree to view its content
                  </span>
                </div>
              )}
            </ResizablePanel>
          </ResizablePanelGroup>
      </ErrorBoundary>
    </StatusProxy>
  );
}
